// One gantt in one content window (TODO §26/§2, §5.2).
//
// **It is always live**, like a Gridd and unlike a diagram: the widget is the renderer and
// the editor, so there is no at-rest representation to cache and no click-into state to
// model. What separates the editable copy from the read-only ones is the `readOnly` prop the
// whole content pipeline already threads — presentation, the print layer and the off-screen
// thumbnail capturer pass true, and SVAR's own `readonly` turns off every drag, resize,
// inline edit and context menu.

import { lazy, Suspense, useCallback, useMemo, useRef } from "react";
import type { IApi } from "@svar-ui/react-gantt";

import { useGanttEditing } from "@/components/deck/ganttEditing";
import { useElementSize } from "@/hooks/useElementSize";
import { ganttFontPx, resolveGanttTheme } from "@/lib/gantt";
import { sheetSwatches } from "@/lib/stylesheet-typography";
import { loadGanttCanvas } from "@/lib/gantt-engine";
import { ganttDocUpdated } from "@/store/ganttSlice";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { selectEffectiveStyleSheet } from "@/store/styleDraftSlice";
import type { GanttLink, GanttTask } from "@/types/gantt";

// Through the engine door so the lazy boundary and the imperative export path share one
// chunk (§7.1).
const GanttCanvas = lazy(() => loadGanttCanvas());

// SVAR's unsubscribe unit is a TAG, not a handler reference — `api.detach(tag)` drops every
// listener filed under it. Distinct from `GanttPanel`'s, so the two never detach each other's.
const EVENT_TAG = "scs-gantt-block";

export function GanttBlock({
  docId,
  readOnly,
}: {
  docId: string;
  readOnly: boolean;
}) {
  const dispatch = useAppDispatch();
  const doc = useAppSelector((s) => s.gantt[docId]);
  const { register, claim, requestEdit } = useGanttEditing();
  // The api this block registered, so the listeners below can be detached from the right
  // object when the widget re-initialises (§8.1).
  const apiRef = useRef<IApi | null>(null);

  // draft ?? committed, like every other content kind: a stylesheet preview restyles the
  // gantt live without a reload (§8.1/§10.1).
  const effectiveStyleSheet = useAppSelector(selectEffectiveStyleSheet);
  const theme = useMemo(
    () => resolveGanttTheme(effectiveStyleSheet),
    [effectiveStyleSheet],
  );
  // §26/§6.3 The sheet's colour table, for the chart's own pair and every per-task override.
  const swatches = useMemo(
    () => sheetSwatches(effectiveStyleSheet),
    [effectiveStyleSheet],
  );

  // §26/§5.2 The window's LOGICAL size, and nothing else.
  //
  // **The gantt lays itself out in the slide's own 1600×900 units, like every other content
  // kind, and lets the canvas transform scale it.** That is the whole of the geometry story
  // now, and the reason it is finally deterministic: `clientWidth` changes only when the
  // layout or the split changes, a ResizeObserver reports exactly that, and no scale factor
  // enters the arithmetic at any point.
  //
  // It used to counter-transform — lay out at `k ×` the window and scale back by `1 / k` — to
  // fix SVAR's pointer maths (§5.6). That bought correct dragging and cost four rounds of
  // sizing bugs, because every number in the chart then lived in screen units that depend on a
  // factor measured somewhere else. See §5.2 in `gantt.todo.md` for the trade that was made
  // and what it costs.
  const [boxRef, box] = useElementSize<HTMLDivElement>();
  const onApi = useCallback(
    (api: IApi | null) => {
      // Detached off the OLD api before the new one is wired: the widget re-initialises on a
      // remount, and a handler left on a dead api is a leak that fires nothing.
      apiRef.current?.detach(EVENT_TAG);
      apiRef.current = api;
      if (api) {
        // §26/§8.6 THE SELECTION EVENT, from the widget itself. The pointer handler on the
        // box below catches a click on the chrome; this catches selection however it happens —
        // keyboard, a context-menu action, `api.exec` from our own panel — which is the half a
        // pointer handler silently misses.
        api.on("select-task", () => claim(docId), { tag: EVENT_TAG });
        // §26/§8.5 A double click on a bar or a grid row makes the widget exec `show-editor`,
        // which sets its `activeTask` and expects an editor to appear. Ours is in the right
        // panel, so without this the gesture changed the selection inside a pane the user
        // could not see and looked like nothing happened at all. `{id: null}` is the widget
        // CLOSING the editor — not a request to open anything, so it is filtered out here
        // rather than left to re-open the panel on every Escape.
        api.on(
          "show-editor",
          ({ id }: { id?: string | number | null }) => {
            if (id == null) return;
            claim(docId);
            requestEdit();
          },
          { tag: EVENT_TAG },
        );
      }
      // §26/§8.6 REGISTER, not claim: a gantt that has merely mounted is not the one the user
      // is working in. The editor mounts every slide at once, so claiming here would make the
      // panel — and the rail's Gantt button — follow "whichever initialised last, anywhere in
      // the deck".
      register(docId, api);
    },
    [docId, register, claim, requestEdit],
  );

  // §8.1 A pointer down anywhere in this block selects it. Two gantts can sit on one slide, so
  // "the chart I am working in" is the only reading a user has of which one the panel edits.
  const claimPanel = useCallback(() => claim(docId), [claim, docId]);
  const onChange = useCallback(
    (next: { tasks: GanttTask[]; links: GanttLink[] }) => {
      if (!doc) return;
      dispatch(
        ganttDocUpdated({
          docId,
          doc: { ...doc, tasks: next.tasks, links: next.links },
        }),
      );
    },
    [dispatch, docId, doc],
  );

  if (!doc) {
    // The reference outlived its body — a bundle imported without its schedules, or a cascade
    // this window has not caught up with. Draw the window, not a crash.
    return (
      <div className="flex min-h-0 min-w-0 flex-1 items-center justify-center rounded-md border border-dashed border-slate-200 bg-slate-50/60 text-xs text-slate-400">
        This gantt's schedule is missing.
      </div>
    );
  }

  // === THE COUNTER-TRANSFORM (§5.2) ==========================================================
  //
  // A slide paints its logical canvas with `transform: scale(k)`, and SVAR does its own
  // pointer arithmetic: its drag handlers take raw `event.clientX - startX` deltas — screen
  // px — and apply them as layout px (`node.style.top = y + "px"`), while reading widths back
  // through `getBoundingClientRect()`. Under `scale(0.55)` a bar therefore moves barely half
  // as far as the cursor, and the progress handle (a rect-derived ratio, which IS
  // scale-invariant) disagrees with it. Exactly the class of bug §21/§6.3 documents for
  // Excalidraw, in a different library, for the same reason.
  //
  // The fix is the one that already exists: give the widget a box whose LAYOUT size is its
  // SCREEN size — lay out at `k ×` the window and scale back by `1 / k` from the same top-left
  // origin — so its two sources of truth agree without it knowing what a slide is.
  //
  // Only when it is interactive. A read-only copy has no pointer maths to get wrong, and the
  // print and presentation paths must lay out in the slide's own logical units so the chart
  // occupies the same space there as in the editor. The two look identical because
  // `GanttCanvas` derives its typography from the fitted row height rather than from fixed
  // px — see §5.3 there; that is what makes this switch invisible.
  const ready = box.width > 0 && box.height > 0;
  // §26/§4.4 The deck's own small-text size, in the slide's logical units — the same units
  // the box above is in, and the same in every render path.
  const fontPx = ganttFontPx(effectiveStyleSheet);

  return (
    <div
      ref={boxRef}
      onPointerDownCapture={readOnly ? undefined : claimPanel}
      className="relative min-h-0 min-w-0 flex-1 overflow-hidden rounded-md border border-slate-200 bg-white"
    >
      {ready && (
        <div style={{ width: box.width, height: box.height }}>
          <Suspense
            fallback={
              <div className="flex h-full w-full items-center justify-center text-xs text-slate-400">
                Loading the schedule…
              </div>
            }
          >
            <GanttCanvas
              doc={doc}
              box={box}
              readOnly={readOnly}
              theme={theme}
              swatches={swatches}
              fontPx={fontPx}
              onApi={readOnly ? undefined : onApi}
              onChange={readOnly ? undefined : onChange}
            />
          </Suspense>
        </div>
      )}
    </div>
  );
}
